// src/components/Navbar.jsx

import React, { useEffect, useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Box,
  useTheme,
  useMediaQuery,
  Avatar,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../services/authService";
import { fetchCurrentUser, clearUser } from "../userSlice";

import RoomIcon from "@mui/icons-material/MeetingRoom";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";

function Navbar() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useSelector((state) => state.user);

  // State to control the mobile drawer
  const [mobileOpen, setMobileOpen] = useState(false);

  // Responsive breakpoint
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    dispatch(fetchCurrentUser());
  }, [dispatch]);

  // Routes where navbar should be hidden
  const hiddenNavbarRoutes = [
    '/login',
    '/register',
    '/organization-choice',
    '/join-pending',
    '/forgot-password',
    '/reset-password'
  ];

  const shouldHideNavbar = () => {
    const currentPath = location.pathname;
    
    // Check exact matches
    if (hiddenNavbarRoutes.includes(currentPath)) {
      return true;
    }
    
    // Check pattern matches
    if (currentPath.startsWith('/join-org/') || 
        currentPath.startsWith('/onboarding/') ||
        currentPath.startsWith('/invite/')) {
      return true;
    }
    
    return false;
  };

  // Don't render navbar if it should be hidden
  if (shouldHideNavbar()) {
    return null;
  }

  function isOrgAdmin() {
    if (!user || !user.organization || !user.organization.users) return false;
    const entry = user.organization.users.find(
      (e) => e.user && e.user._id && e.user._id === user._id
    );
    return entry ? entry.role === "admin" : false;
  }

  const handleLogout = async () => {
    try {
      await logout();
      dispatch(clearUser());
      navigate("/login");
    } catch (err) {
      console.error("Logout error:", err);
    }
  };

  // Toggle for the mobile drawer
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  // Build menu items
  const MenuItems = () => {
    // Same logic for admin or user
    if (!user) {
      return (
        <>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/login"
              onClick={handleDrawerToggle}
            >
              <LoginIcon sx={{ mr: 1 }} />
              <ListItemText primary="Login" />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/register"
              onClick={handleDrawerToggle}
            >
              <PersonAddIcon sx={{ mr: 1 }} />
              <ListItemText primary="Register" />
            </ListItemButton>
          </ListItem>
        </>
      );
    }
    // If user is logged in
    return (
      <>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/rooms"
            onClick={handleDrawerToggle}
          >
            <HomeIcon sx={{ mr: 1 }} />
            <ListItemText primary="Rooms" />
          </ListItemButton>
        </ListItem>
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/create-room"
              onClick={handleDrawerToggle}
            >
              <RoomIcon sx={{ mr: 1 }} />
              <ListItemText primary="Create Room" />
            </ListItemButton>
          </ListItem>
        )}
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/manage-rooms"
              onClick={handleDrawerToggle}
            >
              <SettingsIcon sx={{ mr: 1 }} />
              <ListItemText primary="Manage Rooms" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/my-bookings"
            onClick={handleDrawerToggle}
          >
            <CalendarMonthIcon sx={{ mr: 1 }} />
            <ListItemText primary="My Bookings" />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={Link}
            to="/user-profile"
            onClick={handleDrawerToggle}
          >
            <PersonIcon sx={{ mr: 1 }} />
            <ListItemText primary="User Profile" />
          </ListItemButton>
        </ListItem>
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/all-bookings"
              onClick={handleDrawerToggle}
            >
              <CalendarMonthIcon sx={{ mr: 1 }} />
              <ListItemText primary="All Bookings" />
            </ListItemButton>
          </ListItem>
        )}
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/org-settings"
              onClick={handleDrawerToggle}
            >
              <SettingsIcon sx={{ mr: 1 }} />
              <ListItemText primary="Settings" />
            </ListItemButton>
          </ListItem>
        )}
        {isOrgAdmin() && (
          <ListItem disablePadding>
            <ListItemButton
              component={Link}
              to="/org-members"
              onClick={handleDrawerToggle}
            >
              <GroupIcon sx={{ mr: 1 }} />
              <ListItemText primary="Members" />
            </ListItemButton>
          </ListItem>
        )}
        <ListItem disablePadding>
          <ListItemButton
            onClick={() => {
              handleDrawerToggle();
              handleLogout();
            }}
          >
            <LogoutIcon sx={{ mr: 1 }} />
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </>
    );
  };

  // Drawer content for mobile
  const drawer = (
    <Box
      sx={{ width: 250 }}
      onClick={isMobile ? handleDrawerToggle : undefined}
    >
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Avatar
          src={user?.organization?.logo?.thumbnailUrl || user?.organization?.logo?.url}
          sx={{ width: 32, height: 32 }}
        >
          <BusinessIcon />
        </Avatar>
        <Typography variant="h6">
          {user?.organization?.name || 'Meeting Rooms'}
        </Typography>
      </Box>
      <List sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        <MenuItems />
      </List>
    </Box>
  );

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          {/* Title / Logo */}
          <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar
              src={user?.organization?.logo?.thumbnailUrl || user?.organization?.logo?.url}
              sx={{ width: 40, height: 40 }}
            >
              <BusinessIcon />
            </Avatar>
            <Typography variant="h6">
              {user?.organization?.name || 'Meeting Rooms'}
            </Typography>
          </Box>

          {/* Desktop menu */}
          <Box sx={{ display: { xs: "none", md: "flex" }, gap: 2 }}>
            {user && (
              <Button color="inherit" component={Link} to="/rooms">
                Rooms
              </Button>
            )}
            {user && isOrgAdmin() && (
              <Button color="inherit" component={Link} to="/create-room">
                Create Room
              </Button>
            )}
            {user && isOrgAdmin() && (
              <Button color="inherit" component={Link} to="/manage-rooms">
                Manage Rooms
              </Button>
            )}
            {user ? (
              <>
                <Button color="inherit" component={Link} to="/my-bookings">
                  My Bookings
                </Button>
                <Button color="inherit" component={Link} to="/user-profile">
                  Profile
                </Button>
                {isOrgAdmin() && (
                  <Button color="inherit" component={Link} to="/all-bookings">
                    All Bookings
                  </Button>
                )}
                {isOrgAdmin() && (
                  <Button color="inherit" component={Link} to="/org-settings">
                    Settings
                  </Button>
                )}
                {isOrgAdmin() && (
                  <Button color="inherit" component={Link} to="/org-members">
                    Members
                  </Button>
                )}
                <Button color="inherit" onClick={handleLogout}>
                  Logout
                </Button>
              </>
            ) : (
              <>
                <Button color="inherit" component={Link} to="/login">
                  Login
                </Button>
                <Button color="inherit" component={Link} to="/register">
                  Register
                </Button>
              </>
            )}
          </Box>

          {/* Mobile hamburger icon */}
          <Box sx={{ display: { xs: "flex", md: "none" } }}>
            <IconButton color="inherit" onClick={handleDrawerToggle}>
              <MenuIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Drawer for mobile */}
      <Drawer
        anchor="left"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better mobile performance
        }}
      >
        {drawer}
      </Drawer>
    </Box>
  );
}

export default Navbar;
